import React, {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {toast} from "react-toastify";
import {BeatLoader} from "react-spinners";
import {placeholderAPI, dbConnectionAPI, initializeiFExAuth} from "../../../Api/ifex";
import {FiX} from "react-icons/fi";
import Select from 'react-select';

// Custom styles for react-select to match Tailwind design
const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
        '&:hover': {
            borderColor: '#3b82f6'
        },
        minHeight: '42px',
        backgroundColor: state.isDisabled ? '#f3f4f6' : 'white',
        cursor: state.isDisabled ? 'not-allowed' : 'default'
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#dbeafe' : 'white',
        color: state.isSelected ? 'white' : '#1f2937',
        cursor: 'pointer',
        textAlign: 'left',
        '&:active': {
            backgroundColor: '#3b82f6'
        }
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999
    }),
    placeholder: (base) => ({
        ...base,
        color: '#9ca3af',
        textAlign: 'left'
    }),
    singleValue: (base, state) => ({
        ...base,
        color: state.isDisabled ? '#6b7280' : '#1f2937',
        textAlign: 'left'
    })
};

const PlaceholderEditModal = ({visible, placeholder, onClose, onSuccess, user, isSuperAdmin}) => {
    const [saving, setSaving] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingConnections, setLoadingConnections] = useState(false);
    const [loadingTables, setLoadingTables] = useState(false);
    const [loadingFields, setLoadingFields] = useState(false);

    // Dynamic data
    const [connections, setConnections] = useState([]);
    const [tables, setTables] = useState([]);
    const [fields, setFields] = useState([]);
    const [allPlaceholders, setAllPlaceholders] = useState([]);
    const [fieldPreview, setFieldPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const {register, handleSubmit, watch, setValue, reset, control, formState: {errors, isValid}} = useForm({
        mode: "onChange",
        defaultValues: {
            placeholder_name: '',
            is_active: '1',
            database_connection: '',
            schema: 'dbo',
            table_name: '',
            field_name: '',
            field_format: '',
            placeholder_type: 'standard',
            text_chain_formula: '',
            remarks: '',
        }
    });

    const watchedType = watch('placeholder_type');
    const watchedConnection = watch('database_connection');
    const watchedSchema = watch('schema');
    const watchedTable = watch('table_name');
    const watchedField = watch('field_name');

    // Reset form when placeholder changes or modal opens
    useEffect(() => {
        if (visible && placeholder) {
            setInitialLoading(true);

            // Convert database_connection to string for the form (it's stored as int but form uses string)
            const dbConnectionValue = placeholder.database_connection ? String(placeholder.database_connection) : '';

            reset({
                placeholder_name: placeholder.placeholder_name || '',
                is_active: placeholder.is_active ? '1' : '0',
                database_connection: dbConnectionValue,
                schema: placeholder.schema || 'dbo',
                table_name: placeholder.table_name || '',
                field_name: placeholder.field_name || '',
                field_format: placeholder.field_format || '',
                placeholder_type: placeholder.placeholder_type || 'standard',
                text_chain_formula: placeholder.text_chain_formula || '',
                remarks: placeholder.remarks || '',
            });

            // Pre-load tables and fields if database_connection exists
            if (placeholder.database_connection && isSuperAdmin) {
                const loadData = async () => {
                    await fetchTables(placeholder.database_connection, placeholder.schema || 'dbo');
                    if (placeholder.table_name) {
                        await fetchFields(placeholder.database_connection, placeholder.table_name, placeholder.schema || 'dbo');
                    }
                };
                loadData();
            }
        }
    }, [visible, placeholder, reset]);

    useEffect(() => {
        if (visible) {
            fetchConnections();
            if (isSuperAdmin && watchedType === 'textchain') {
                fetchAllPlaceholders();
            }
        }
    }, [visible]);

    // Re-set database_connection value after connections are loaded and mark as ready
    useEffect(() => {
        if (visible && !loadingConnections && placeholder) {
            // Set the database_connection value if connections are loaded
            if (connections.length > 0) {
                setValue('database_connection', String(placeholder.database_connection || ''));
            }

            // Check if we need to wait for tables/fields or can show form now
            const needsTables = placeholder.database_connection && isSuperAdmin;
            const needsFields = needsTables && placeholder.table_name;

            if (!needsTables) {
                // No tables/fields needed, we're ready
                setInitialLoading(false);
            } else if (needsTables && !needsFields && tables.length > 0) {
                // Only needed tables, and they're loaded
                setInitialLoading(false);
            } else if (needsFields && fields.length > 0) {
                // Needed fields, and they're loaded
                setInitialLoading(false);
            } else if (!placeholder.database_connection) {
                // No database connection at all
                setInitialLoading(false);
            } else if (!loadingTables && !loadingFields) {
                // API calls finished but no data (empty database)
                setInitialLoading(false);
            }
        }
    }, [connections, tables, fields, placeholder, setValue, visible, isSuperAdmin, loadingConnections, loadingTables, loadingFields]);

    useEffect(() => {
        if (watchedConnection && watchedSchema && isSuperAdmin) {
            fetchTables(watchedConnection, watchedSchema);
        }
    }, [watchedConnection, watchedSchema]);

    useEffect(() => {
        if (watchedTable && watchedConnection && watchedSchema && isSuperAdmin) {
            fetchFields(watchedConnection, watchedTable, watchedSchema);
        }
    }, [watchedTable, watchedSchema]);

    // Fetch field preview when field is selected
    useEffect(() => {
        if (watchedField && watchedConnection && watchedTable && watchedSchema && isSuperAdmin) {
            fetchFieldPreview(watchedConnection, watchedTable, watchedField, watchedSchema);
        } else {
            setFieldPreview(null);
        }
    }, [watchedField]);

    const fetchConnections = async () => {
        setLoadingConnections(true);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setLoadingConnections(false);
                setInitialLoading(false);
                return;
            }

            const response = await dbConnectionAPI.getAll();
            if (response.data.success) {
                setConnections(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching connections:', error);
            toast.error('Fehler beim Laden der Verbindungen');
            setInitialLoading(false); // Show form even if connections fail
        } finally {
            setLoadingConnections(false);
        }
    };

    const fetchTables = async (connectionId, schema = 'dbo') => {
        if (!connectionId) return;

        setLoadingTables(true);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setLoadingTables(false);
                return;
            }

            const response = await dbConnectionAPI.getTables(connectionId, schema);
            if (response.data.success) {
                // Remove duplicates and sort tables alphabetically
                const uniqueTables = Array.from(new Set(response.data.tables || []));
                const sortedTables = uniqueTables.sort((a, b) =>
                    a.localeCompare(b, undefined, {sensitivity: 'base'})
                );
                setTables(sortedTables);
            }
        } catch (error) {
            console.error('Error fetching tables:', error);
            toast.error('Fehler beim Laden der Tabellen');
        } finally {
            setLoadingTables(false);
        }
    };

    const fetchFields = async (connectionId, tableName, schema = 'dbo') => {
        if (!connectionId || !tableName) return;

        setLoadingFields(true);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setLoadingFields(false);
                return;
            }

            const response = await dbConnectionAPI.getTableFields(connectionId, tableName, schema);
            if (response.data.success) {
                // Remove duplicates based on field name and sort alphabetically
                const fieldsArray = response.data.fields || [];
                const uniqueFields = fieldsArray.filter((field, index, self) => {
                    const fieldName = field.name || field;
                    return index === self.findIndex(f => (f.name || f) === fieldName);
                });
                const sortedFields = uniqueFields.sort((a, b) => {
                    const nameA = a.name || a;
                    const nameB = b.name || b;
                    return nameA.localeCompare(nameB, undefined, {sensitivity: 'base'});
                });
                setFields(sortedFields);
            }
        } catch (error) {
            console.error('Error fetching fields:', error);
            toast.error('Fehler beim Laden der Felder');
        } finally {
            setLoadingFields(false);
        }
    };

    const fetchFieldPreview = async (connectionId, tableName, fieldName, schema = 'dbo') => {
        setLoadingPreview(true);
        setFieldPreview(null);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                return;
            }

            const response = await dbConnectionAPI.getFieldPreview(
                connectionId,
                tableName,
                fieldName,
                schema,
                5
            );

            if (response.data.success) {
                setFieldPreview(response.data.samples);
            }
        } catch (error) {
            console.error('Error fetching field preview:', error);
            // Don't show error toast for preview failures (non-critical)
        } finally {
            setLoadingPreview(false);
        }
    };

    const fetchAllPlaceholders = async () => {
        try {
            const response = await placeholderAPI.getAll({per_page: 1000});
            if (response.data.success) {
                setAllPlaceholders(response.data.data.filter(p => p.id !== placeholder.id));
            }
        } catch (error) {
            console.error('Error fetching placeholders:', error);
        }
    };

    const onSubmit = async (data) => {
        setSaving(true);
        try {
            // Convert is_active to boolean
            const payload = {
                ...data,
                is_active: data.is_active === '1',
            };

            await placeholderAPI.update(placeholder.id, payload);
            toast.success('Platzhalter erfolgreich aktualisiert');
            onSuccess();
        } catch (error) {
            console.error('Error updating placeholder:', error);
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach(err => {
                    toast.error(err[0]);
                });
            } else {
                toast.error('Fehler beim Aktualisieren des Platzhalters');
            }
        } finally {
            setSaving(false);
        }
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-modalBackDropColor bg-opacity-60"
                onClick={onClose}
            ></div>
            <div className="relative z-10 bg-white rounded-xl shadow-2xl w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-borderBlue p-6 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-text">Platzhalter bearbeiten</h2>
                    <button onClick={onClose} className="text-grey hover:text-text">
                        <FiX size={24} />
                    </button>
                </div>

                {/* Loading State */}
                {initialLoading && (
                    <div className="p-6 flex items-center justify-center min-h-[400px]">
                        <div className="text-center">
                            <BeatLoader size={15} color="#3A46A9" />
                            <p className="text-grey mt-4">Lade Daten...</p>
                        </div>
                    </div>
                )}

                {/* Form */}
                {!initialLoading && (
                <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Placeholder Name */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-text mb-2">
                                Platzhalter-Name *
                                {!isSuperAdmin && <span className="text-xs text-grey ml-2">(Nur Ansicht)</span>}
                            </label>
                            <input
                                type="text"
                                className={`w-full px-3 py-2 border rounded ${errors.placeholder_name ? 'border-cancel' : 'border-borderBlue'} ${!isSuperAdmin ? 'bg-silver cursor-not-allowed text-grey' : ''}`}
                                {...register('placeholder_name', {
                                    required: 'Platzhalter-Name ist erforderlich',
                                    pattern: {
                                        value: /^\[([A-Z]+_[A-Za-z0-9]+)\]$/,
                                        message: 'Format: [PRÄFIX_Name] (z.B. [M_Vorname])'
                                    }
                                })}
                                disabled={!isSuperAdmin}
                            />
                            {errors.placeholder_name && <p className="text-cancel text-sm mt-1">{errors.placeholder_name.message}</p>}
                        </div>

                        {/* Active Status */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">
                                Status *
                            </label>
                            <select
                                className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none"
                                {...register('is_active', {required: true})}
                            >
                                <option value="1">Aktiv</option>
                                <option value="0">Inaktiv</option>
                            </select>
                        </div>

                        {/* Placeholder Type */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">
                                Typ *
                                {!isSuperAdmin && <span className="text-xs text-grey ml-2">(Nur Ansicht)</span>}
                            </label>
                            <select
                                className={`w-full px-3 py-2 border border-borderBlue rounded ${!isSuperAdmin ? 'bg-silver cursor-not-allowed text-grey' : ''}`}
                                {...register('placeholder_type', {required: true})}
                                disabled={!isSuperAdmin}
                            >
                                <option value="standard">Standard</option>
                                <option value="required">Pflichtfeld</option>
                                <option value="formfield">Formularfeld</option>
                                <option value="textchain">Textkette</option>
                            </select>
                        </div>

                        {/* Database Connection */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">
                                Datenbankverbindung
                                {!isSuperAdmin && <span className="text-xs text-grey ml-2">(Nur Ansicht)</span>}
                            </label>
                            <select
                                className={`w-full px-3 py-2 border border-borderBlue rounded ${!isSuperAdmin ? 'bg-silver cursor-not-allowed text-grey' : ''}`}
                                {...register('database_connection')}
                                disabled={!isSuperAdmin || loadingConnections}
                            >
                                <option value="">-- Wählen --</option>
                                {connections.map(conn => (
                                    <option key={conn.id} value={conn.id}>{conn.connection_name || conn.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Schema Selection */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">
                                Schema <span className="text-cancel">*</span>
                                {!isSuperAdmin && <span className="text-xs text-grey ml-2">(Nur Ansicht)</span>}
                            </label>
                            <select
                                className={`w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none ${!isSuperAdmin ? 'bg-silver cursor-not-allowed text-grey' : ''}`}
                                {...register('schema', {required: 'Schema ist erforderlich'})}
                                disabled={!isSuperAdmin || !watchedConnection}
                            >
                                <option value="dbo">dbo</option>
                                <option value="dgg">dgg</option>
                            </select>
                            {errors.schema && <p className="text-cancel text-sm mt-1">{errors.schema.message}</p>}
                        </div>

                        {/* Table Name */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">
                                Tabelle
                                {!isSuperAdmin && <span className="text-xs text-grey ml-2">(Nur Ansicht)</span>}
                            </label>
                            <Controller
                                name="table_name"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={tables.map(table => ({ value: table, label: table }))}
                                        value={field.value ? { value: field.value, label: field.value } : null}
                                        onChange={(option) => field.onChange(option?.value || '')}
                                        styles={selectStyles}
                                        isDisabled={!isSuperAdmin || loadingTables || !watchedConnection}
                                        isLoading={loadingTables}
                                        placeholder="-- Wählen --"
                                        isClearable
                                        isSearchable
                                        noOptionsMessage={() => "Keine Tabellen gefunden"}
                                    />
                                )}
                            />
                            {loadingTables && <p className="text-sm text-grey mt-1">Lade Tabellen...</p>}
                        </div>

                        {/* Field Name */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">
                                Feld
                                {!isSuperAdmin && <span className="text-xs text-grey ml-2">(Nur Ansicht)</span>}
                            </label>
                            <Controller
                                name="field_name"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={fields.map(f => {
                                            const fieldName = f.name || f;
                                            const fieldType = f.type || '';
                                            const maxLength = f.maxLength ? `(${f.maxLength})` : '';
                                            const nullable = f.nullable === false ? ' *' : '';

                                            return {
                                                value: fieldName,
                                                label: `${fieldName} ${fieldType}${maxLength}${nullable}`.trim()
                                            };
                                        })}
                                        value={field.value ? (() => {
                                            const selectedField = fields.find(f => (f.name || f) === field.value);
                                            if (selectedField) {
                                                const fieldType = selectedField.type || '';
                                                const maxLength = selectedField.maxLength ? `(${selectedField.maxLength})` : '';
                                                const nullable = selectedField.nullable === false ? ' *' : '';
                                                return {
                                                    value: field.value,
                                                    label: `${field.value} ${fieldType}${maxLength}${nullable}`.trim()
                                                };
                                            }
                                            return { value: field.value, label: field.value };
                                        })() : null}
                                        onChange={(option) => field.onChange(option?.value || '')}
                                        styles={selectStyles}
                                        isDisabled={!isSuperAdmin || loadingFields || !watchedTable}
                                        isLoading={loadingFields}
                                        placeholder="-- Wählen --"
                                        isClearable
                                        isSearchable
                                        noOptionsMessage={() => "Keine Felder gefunden"}
                                    />
                                )}
                            />
                            {loadingFields && <p className="text-sm text-grey mt-1">Lade Felder...</p>}

                            {/* Field Preview Panel */}
                            {watchedField && isSuperAdmin && (
                                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded">
                                    <p className="text-xs font-semibold text-gray-700 mb-1">Beispielwerte:</p>
                                    {loadingPreview ? (
                                        <p className="text-xs text-gray-500">Lade Vorschau...</p>
                                    ) : fieldPreview && fieldPreview.length > 0 ? (
                                        <ul className="text-xs text-gray-600 space-y-0.5">
                                            {fieldPreview.map((sample, idx) => (
                                                <li key={idx} className="font-mono">• "{sample}"</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-gray-500">Keine Beispielwerte verfügbar</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Field Format */}
                        <div>
                            <label className="block text-sm font-medium text-text mb-2">
                                Feldformat
                                {!isSuperAdmin && <span className="text-xs text-grey ml-2">(Nur Ansicht)</span>}
                            </label>
                            <select
                                className={`w-full px-3 py-2 border border-borderBlue rounded ${!isSuperAdmin ? 'bg-silver cursor-not-allowed text-grey' : ''}`}
                                {...register('field_format')}
                                disabled={!isSuperAdmin}
                            >
                                <option value="">-- Wählen --</option>
                                <option value="text">Text</option>
                                <option value="date_de">Datum (TT.MM.JJJJ)</option>
                                <option value="plz">PLZ (00000)</option>
                                <option value="number">Zahl</option>
                                <option value="currency">Währung</option>
                            </select>
                        </div>

                        {/* Text Chain Formula */}
                        {watchedType === 'textchain' && (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text mb-2">
                                    Textketten-Formel
                                    {!isSuperAdmin && <span className="text-xs text-grey ml-2">(Nur Ansicht)</span>}
                                </label>
                                <textarea
                                    className={`w-full px-3 py-2 border border-borderBlue rounded ${!isSuperAdmin ? 'bg-silver cursor-not-allowed text-grey' : ''}`}
                                    rows="3"
                                    placeholder="z.B. [M_Vorname] + ' ' + [M_Nachname]"
                                    {...register('text_chain_formula')}
                                    disabled={!isSuperAdmin}
                                />
                                <p className="text-xs text-grey mt-1">
                                    Verwenden Sie andere Platzhalter mit + für Verkettung
                                </p>
                            </div>
                        )}

                        {/* Remarks */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-text mb-2">
                                Bemerkungen
                            </label>
                            <textarea
                                className="w-full px-3 py-2 border border-borderBlue rounded text-text focus:border-mainBlue focus:outline-none"
                                rows="3"
                                {...register('remarks')}
                            />
                        </div>
                    </div>

                    {/* Metadata Display */}
                    <div className="mt-6 p-4 bg-offWhite rounded">
                        <h3 className="text-sm font-medium text-text mb-2">Metadata</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            <div><strong>ID:</strong> {placeholder.id}</div>
                            <div><strong>Erstellt am:</strong> {placeholder.created_at ? new Date(placeholder.created_at).toLocaleString('de-DE') : '-'}</div>
                            <div><strong>Erstellt von:</strong> {placeholder.created_by || '-'}</div>
                            <div><strong>Letzter Zugriff:</strong> {placeholder.last_accessed_at ? new Date(placeholder.last_accessed_at).toLocaleString('de-DE') : '-'}</div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-borderBlue">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 bg-whiteDark hover:bg-silver text-text rounded transition"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !isValid}
                            className={`px-6 py-2 rounded text-white flex items-center gap-2 ${
                                saving || !isValid ? 'bg-silver text-grey cursor-not-allowed' : 'bg-mainBlue hover:bg-opacity-90 transition'
                            }`}
                        >
                            {saving && <BeatLoader size={8} color="#ffffff" />}
                            {saving ? 'Speichere...' : 'Speichern'}
                        </button>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
};

export default PlaceholderEditModal;
